/**
 * 用户缓存服务
 * 负责在用户登录/初始化时管理和验证音乐缓存
 */

import cacheValidator from '@/utils/cacheValidator';

class UserCacheService {
  constructor() {
    this.initialized = false;
    this.currentUserId = null;
  }

  /**
   * 用户登录时初始化缓存
   * @param {Object} userInfo - 用户信息
   * @returns {Promise<Object>} 初始化结果
   */
  async initializeUserCache(userInfo) {
    if (!userInfo || !userInfo.id) {
      console.warn('👤 [用户缓存服务] 用户信息无效，跳过缓存初始化');
      return { skipped: true, reason: 'invalid_user' };
    }

    const userId = String(userInfo.id);
    console.log(`👤 [用户缓存服务] 开始为用户 ${userId} 初始化缓存...`);

    try {
      // 🔥 移除设置 AudioController 用户ID的逻辑
      // AudioController 现在使用 tokenService.getCurrentUserId() 动态获取用户ID
      // 这样更灵活，可以处理用户切换等情况
      
      // 2. 如果用户切换，记录日志但不清理其他用户的缓存
      if (this.currentUserId && this.currentUserId !== userId) {
        console.log(`👤 [用户缓存服务] 用户切换: ${this.currentUserId} -> ${userId}`);
        console.log('👤 [用户缓存服务] 保留所有用户的缓存，不进行清理');
        // 注意：不再清理其他用户的缓存，让每个用户的缓存独立保存
      }

      // 3. 验证当前用户的缓存有效性
      const validationResult = await cacheValidator.validateUserCaches(userId);
      
      // 4. 获取缓存统计信息
      const cacheStats = await cacheValidator.getCacheStats(userId);

      // 5. 更新当前用户ID
      this.currentUserId = userId;
      this.initialized = true;

      const result = {
        userId: userId,
        initialized: true,
        validation: validationResult,
        stats: cacheStats,
        timestamp: Date.now()
      };

      console.log(`👤 [用户缓存服务] 用户 ${userId} 缓存初始化完成`);
      console.log(`👤 [用户缓存服务] 缓存统计: ${cacheStats.userCaches} 个用户缓存, ${cacheStats.totalSizeMB}MB`);
      
      // 获取缓存清理配置信息
      const cleanupConfig = cacheValidator.getCleanupConfig();
      console.log(`👤 [用户缓存服务] 缓存清理配置:`, cleanupConfig);
      
      if (validationResult.cleanupResults && validationResult.cleanupResults.cleaned > 0) {
        console.log(`👤 [用户缓存服务] 清理了 ${validationResult.cleanupResults.cleaned} 个缓存`);
      } else {
        console.log(`👤 [用户缓存服务] 未清理任何缓存（配置已关闭自动清理，永久保存所有缓存）`);
      }

      return result;

    } catch (error) {
      console.error(`👤 [用户缓存服务] 用户 ${userId} 缓存初始化失败:`, error);
      return {
        userId: userId,
        initialized: false,
        error: error.message,
        timestamp: Date.now()
      };
    }
  }

  /**
   * 用户登出时清理缓存相关状态
   */
  async cleanupOnLogout() {
    console.log('👤 [用户缓存服务] 用户登出，清理缓存状态...');
    
    try {
      // 🔥 移除重置 AudioController 用户ID的逻辑
      // AudioController 使用 tokenService 动态获取用户ID，登出时 tokenService 会自动处理
      
      // 重置服务状态
      this.currentUserId = null;
      this.initialized = false;
      
      console.log('👤 [用户缓存服务] 登出清理完成');
      
    } catch (error) {
      console.error('👤 [用户缓存服务] 登出清理失败:', error);
    }
  }

  /**
   * 清理其他用户的缓存
   * @param {string} currentUserId - 当前用户ID
   */
  async cleanupOtherUserCaches(currentUserId) {
    try {
      console.log(`👤 [用户缓存服务] 开始清理其他用户的缓存...`);
      const cleanupResult = await cacheValidator.cleanupOtherUserCaches(currentUserId);
      
      if (cleanupResult.cleaned > 0) {
        console.log(`👤 [用户缓存服务] 清理了 ${cleanupResult.cleaned} 个其他用户的缓存`);
      } else {
        console.log('👤 [用户缓存服务] 没有其他用户的缓存需要清理');
      }
      
      return cleanupResult;
    } catch (error) {
      console.error('👤 [用户缓存服务] 清理其他用户缓存失败:', error);
      return { cleaned: 0, failed: 0, errors: [error.message] };
    }
  }

  /**
   * 手动验证用户缓存
   * @param {boolean} forceValidation - 是否强制验证
   * @returns {Promise<Object>} 验证结果
   */
  async validateCurrentUserCache(forceValidation = false) {
    if (!this.currentUserId) {
      console.warn('👤 [用户缓存服务] 没有当前用户，无法验证缓存');
      return { skipped: true, reason: 'no_user' };
    }

    try {
      console.log(`👤 [用户缓存服务] 开始验证用户 ${this.currentUserId} 的缓存...`);
      const result = await cacheValidator.validateUserCaches(this.currentUserId, forceValidation);
      
      if (result.cleanupResults && result.cleanupResults.cleaned > 0) {
        console.log(`👤 [用户缓存服务] 验证完成，清理了 ${result.cleanupResults.cleaned} 个无效缓存`);
      }
      
      return result;
    } catch (error) {
      console.error('👤 [用户缓存服务] 缓存验证失败:', error);
      throw error;
    }
  }

  /**
   * 获取当前用户的缓存统计
   * @returns {Promise<Object>} 缓存统计信息
   */
  async getCurrentUserCacheStats() {
    if (!this.currentUserId) {
      return {
        userId: null,
        userCaches: 0,
        orphanCaches: 0,
        otherUserCaches: 0,
        totalCaches: 0,
        totalSize: 0,
        totalSizeMB: '0.00'
      };
    }

    try {
      return await cacheValidator.getCacheStats(this.currentUserId);
    } catch (error) {
      console.error('👤 [用户缓存服务] 获取缓存统计失败:', error);
      throw error;
    }
  }

  /**
   * 检查服务是否已初始化
   * @returns {boolean} 是否已初始化
   */
  isInitialized() {
    return this.initialized && this.currentUserId !== null;
  }

  /**
   * 获取当前用户ID
   * @returns {string|null} 当前用户ID
   */
  getCurrentUserId() {
    return this.currentUserId;
  }

  /**
   * 清理所有缓存（危险操作）
   * @returns {Promise<boolean>} 是否成功
   */
  async clearAllCaches() {
    try {
      console.warn('⚠️ [用户缓存服务] 即将清理所有音乐缓存！');
      
      // 动态导入musicCache以避免循环依赖
      const { default: musicCache } = await import('@/utils/musicCache');
      await musicCache.clear();
      
      console.log('🗑️ [用户缓存服务] 所有缓存已清理');
      return true;
    } catch (error) {
      console.error('❌ [用户缓存服务] 清理所有缓存失败:', error);
      return false;
    }
  }
}

// 创建全局单例
const userCacheService = new UserCacheService();

export default userCacheService;




